import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileSpreadsheet, Loader2, Download, FileText, Info } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/ui/page-header";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const yearStart = () => { const d = new Date(); return `${d.getFullYear()}-01-01`; };
const weekStart = () => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().slice(0, 10); };

const JENIS_OPTIONS = [
    { value: "all", label: "Semua Transaksi" },
    { value: "spk", label: "SPK / Job Order" },
    { value: "bon_kain", label: "Bon Kain" },
    { value: "bon_peralatan", label: "Bon Peralatan" },
    { value: "setor", label: "Setor Barang" },
    { value: "qc", label: "Pemeriksaan QC" },
    { value: "kasbon", label: "Kasbon" },
    { value: "invoice", label: "Invoice" },
];

const PRESETS = [
    { key: "all", label: "Semua Waktu", getRange: () => ({ mulai: "", akhir: "" }) },
    { key: "year", label: "Tahun Ini", getRange: () => ({ mulai: yearStart(), akhir: today() }) },
    { key: "month", label: "Bulan Ini", getRange: () => ({ mulai: monthStart(), akhir: today() }) },
    { key: "week", label: "7 Hari Terakhir", getRange: () => ({ mulai: weekStart(), akhir: today() }) },
];

export default function LaporanExportPage() {
    const [vendors, setVendors] = useState([]);
    const [vendorId, setVendorId] = useState("all");
    // v1.8.10 — Default "Semua Waktu" (tanggal kosong) biar historis 2023+ tidak ter-skip
    const [tanggalMulai, setTanggalMulai] = useState("");
    const [tanggalAkhir, setTanggalAkhir] = useState("");
    const [presetKey, setPresetKey] = useState("all");
    const [jenis, setJenis] = useState("all");
    const [downloading, setDownloading] = useState(false);
    const [previewCount, setPreviewCount] = useState(null); // { total, by_jenis }
    const [loadingPreview, setLoadingPreview] = useState(false);

    useEffect(() => { (async () => { try { const v = await api.get("/vendors"); setVendors(v.data); } catch (e) { /* silent */ } })(); }, []);

    // Preview count live-update saat filter berubah
    useEffect(() => {
        let cancel = false;
        (async () => {
            setLoadingPreview(true);
            try {
                const params = new URLSearchParams();
                if (vendorId !== "all") params.set("vendor_id", vendorId);
                if (tanggalMulai) params.set("tanggal_mulai", tanggalMulai);
                if (tanggalAkhir) params.set("tanggal_akhir", tanggalAkhir);
                const res = await api.get(`/export/preview-count?${params.toString()}`);
                if (!cancel) setPreviewCount(res.data);
            } catch (e) { if (!cancel) setPreviewCount(null); }
            finally { if (!cancel) setLoadingPreview(false); }
        })();
        return () => { cancel = true; };
    }, [vendorId, tanggalMulai, tanggalAkhir]);

    const applyPreset = (key) => {
        setPresetKey(key);
        const p = PRESETS.find((x) => x.key === key);
        if (p) { const r = p.getRange(); setTanggalMulai(r.mulai); setTanggalAkhir(r.akhir); }
    };

    const onDateChange = (which, val) => {
        setPresetKey("custom");
        if (which === "mulai") setTanggalMulai(val); else setTanggalAkhir(val);
    };

    const filteredCount = useMemo(() => {
        if (!previewCount) return null;
        if (jenis === "all") return previewCount.total || 0;
        return previewCount.by_jenis?.[jenis] || 0;
    }, [previewCount, jenis]);

    const doDownload = async () => {
        setDownloading(true);
        try {
            const params = new URLSearchParams();
            if (vendorId !== "all") params.set("vendor_id", vendorId);
            if (tanggalMulai) params.set("tanggal_mulai", tanggalMulai);
            if (tanggalAkhir) params.set("tanggal_akhir", tanggalAkhir);
            if (jenis) params.set("jenis_transaksi", jenis);
            const res = await api.get(`/export/excel?${params.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = `Laporan_Transaksi_${today()}.xlsx`; a.click();
            URL.revokeObjectURL(url);
            toast.success("Laporan Excel berhasil diunduh");
        } catch (e) { toast.error(errorMessage(e) || "Gagal export"); }
        finally { setDownloading(false); }
    };

    const rangeLabel = (!tanggalMulai && !tanggalAkhir) ? "Semua Waktu"
        : `${tanggalMulai || "awal"} s/d ${tanggalAkhir || "hari ini"}`;

    return (
        <div data-testid="laporan-page">
            <PageHeader
                title="Laporan / Export"
                description="Export data transaksi ke file Excel. Tiap jenis transaksi masuk ke sheet terpisah."
            />

            <div className="space-y-4 md:space-y-5 max-w-3xl">
                {/* Section Filter */}
                <section
                    className="rounded-xl bg-white p-5 md:p-6"
                    style={{ border: "1px solid var(--border)" }}
                >
                    <div className="flex items-start gap-3 mb-5">
                        <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ background: "var(--apple-blue-soft)", color: "var(--apple-blue)" }}
                        >
                            <FileText className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="font-semibold text-base" style={{ color: "var(--text-primary)" }}>Pilih Data & Periode</h2>
                            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Default <strong>Semua Waktu</strong> — pakai preset atau atur rentang manual.</p>
                        </div>
                    </div>

                    {/* Preset shortcuts */}
                    <div className="mb-4" data-testid="laporan-presets">
                        <Label className="text-xs">Preset Cepat</Label>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                            {PRESETS.map((p) => (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => applyPreset(p.key)}
                                    data-testid={`laporan-preset-${p.key}`}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${presetKey === p.key ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-700 border-slate-300 hover:border-teal-400"}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                            {presetKey === "custom" && (
                                <span className="text-xs px-3 py-1.5 rounded-full border bg-amber-50 text-amber-700 border-amber-300">Custom</span>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label>Vendor</Label>
                            <Select value={vendorId} onValueChange={setVendorId}>
                                <SelectTrigger data-testid="laporan-form-vendor" className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Semua Vendor</SelectItem>
                                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Jenis Transaksi</Label>
                            <Select value={jenis} onValueChange={setJenis}>
                                <SelectTrigger data-testid="laporan-form-jenis" className="mt-1.5 rounded-lg"><SelectValue /></SelectTrigger>
                                <SelectContent>{JENIS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Tanggal Mulai <span className="text-slate-400 font-normal">(kosong = awal data)</span></Label>
                            <Input type="date" value={tanggalMulai} onChange={(e) => onDateChange("mulai", e.target.value)} data-testid="laporan-form-mulai" className="mt-1.5 rounded-lg" />
                        </div>
                        <div>
                            <Label>Tanggal Akhir <span className="text-slate-400 font-normal">(kosong = hari ini)</span></Label>
                            <Input type="date" value={tanggalAkhir} onChange={(e) => onDateChange("akhir", e.target.value)} data-testid="laporan-form-akhir" className="mt-1.5 rounded-lg" />
                        </div>
                    </div>

                    {/* Preview count */}
                    <div
                        className="mt-5 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap"
                        style={{ background: "var(--apple-blue-soft)", border: "1px solid var(--apple-blue-border)" }}
                        data-testid="laporan-preview-count"
                    >
                        <div className="text-xs space-y-0.5">
                            <div className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>
                                {loadingPreview ? "Menghitung…" :
                                    filteredCount != null ? `Akan diexport: ${filteredCount.toLocaleString("id-ID")} transaksi` : "—"}
                            </div>
                            <div style={{ color: "var(--text-muted)" }}>Periode: {rangeLabel} · Vendor: {vendorId === "all" ? "semua" : (vendors.find((v) => v.id === vendorId)?.nama || "-")}</div>
                            {previewCount && jenis === "all" && previewCount.total > 0 && (
                                <div className="pt-1 flex flex-wrap gap-x-3" style={{ color: "var(--text-muted)" }}>
                                    {Object.entries(previewCount.by_jenis || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                                        <span key={k}>{k}: <b>{v}</b></span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="pt-5 mt-5 border-t flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--border-soft)" }}>
                        <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
                            <FileSpreadsheet className="w-4 h-4" style={{ color: "var(--apple-blue)" }} />
                            Format: .xlsx (Excel 2007+)
                        </div>
                        <Button
                            onClick={doDownload}
                            disabled={downloading || filteredCount === 0}
                            data-testid="laporan-download-btn"
                            className="rounded-lg gap-2 text-white"
                            style={{ background: "var(--apple-blue)" }}
                        >
                            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Excel
                        </Button>
                    </div>
                </section>

                {/* Info Section */}
                <section
                    className="rounded-xl p-5"
                    style={{ background: "var(--apple-blue-soft)", border: "1px solid var(--apple-blue-border)" }}
                >
                    <div className="flex items-start gap-3">
                        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--apple-blue)" }} />
                        <div className="text-xs space-y-1.5" style={{ color: "var(--text-secondary)" }}>
                            <div className="font-semibold text-sm mb-1" style={{ color: "var(--text-primary)" }}>Info Sheet Excel</div>
                            <div>• "Semua Transaksi" → 8 sheet terpisah (SPK, Bon Kain, Bon Peralatan, Setor, QC, Kasbon, Invoice).</div>
                            <div>• Filter tanggal mengacu field per jenis (setor pakai tanggal_setor, invoice pakai tanggal_tutup).</div>
                            <div>• Kolom "Locked" menandai transaksi yang sudah masuk invoice tertutup (immutable).</div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}
